import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Building2, Bike } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#0a0f1d] border border-white/15 rounded-sm px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#d4af37] transition-colors duration-300";

const clientServices = [
  "Salary-Based Hiring Solutions",
  "Permanent Recruitment & Staffing",
  "Contract & Temporary Staffing",
  "Skilled & Unskilled Manpower Supply",
  "Warehouse & Logistics Staff",
  "Office & Administrative Staff",
  "Payroll & HR Support",
];

const riderRoles = ["Van Delivery Associate", "Delivery Assistant", "In-Store Staff", "Warehouse / Sorting Staff", "Other"];

const ClientForm = () => {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", service: clientServices[0], headcount: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/enquiries", form);
      toast.success(data.message);
      setForm({ name: "", company: "", phone: "", email: "", service: clientServices[0], headcount: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="client-enquiry-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
      <input data-testid="client-name-input" required placeholder="Your Name *" value={form.name} onChange={set("name")} className={inputCls} />
      <input data-testid="client-company-input" placeholder="Company Name" value={form.company} onChange={set("company")} className={inputCls} />
      <input data-testid="client-phone-input" required placeholder="Phone Number *" value={form.phone} onChange={set("phone")} className={inputCls} />
      <input data-testid="client-email-input" type="email" placeholder="Email Address" value={form.email} onChange={set("email")} className={inputCls} />
      <select data-testid="client-service-select" value={form.service} onChange={set("service")} className={inputCls}>
        {clientServices.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <input data-testid="client-headcount-input" placeholder="Manpower Needed (e.g. 15 riders)" value={form.headcount} onChange={set("headcount")} className={inputCls} />
      <textarea
        data-testid="client-message-input"
        placeholder="Tell us about your requirement — volumes, location, timelines..."
        value={form.message}
        onChange={set("message")}
        rows={4}
        className={`${inputCls} sm:col-span-2 resize-none`}
      />
      <button
        data-testid="btn-submit-client-form"
        type="submit"
        disabled={loading}
        className="sm:col-span-2 inline-flex items-center justify-center gap-3 bg-[#d4af37] text-[#0a0f1d] font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm hover:bg-[#f59e0b] disabled:opacity-60 transition-colors duration-300"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {loading ? "Sending..." : "Request Manpower"}
      </button>
    </form>
  );
};

const RiderForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", city: "", role: riderRoles[0], aadhar_verified: "yes", experience: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/applications", form);
      toast.success(data.message);
      setForm({ name: "", phone: "", city: "", role: riderRoles[0], aadhar_verified: "yes", experience: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="rider-application-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
      <input data-testid="rider-name-input" required placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
      <input data-testid="rider-phone-input" required placeholder="Mobile Number *" value={form.phone} onChange={set("phone")} className={inputCls} />
      <input data-testid="rider-city-input" required placeholder="City / Area *" value={form.city} onChange={set("city")} className={inputCls} />
      <select data-testid="rider-role-select" value={form.role} onChange={set("role")} className={inputCls}>
        {riderRoles.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      <select data-testid="rider-aadhar-select" value={form.aadhar_verified} onChange={set("aadhar_verified")} className={inputCls}>
        <option value="yes">Aadhar Card: Available</option>
        <option value="no">Aadhar Card: Not Available</option>
      </select>
      <input data-testid="rider-experience-input" placeholder="Experience (e.g. 2 years delivery)" value={form.experience} onChange={set("experience")} className={inputCls} />
      <textarea
        data-testid="rider-message-input"
        placeholder="Anything else — vehicle owned, areas you know, current status..."
        value={form.message}
        onChange={set("message")}
        rows={4}
        className={`${inputCls} sm:col-span-2 resize-none`}
      />
      <button
        data-testid="btn-submit-rider-form"
        type="submit"
        disabled={loading}
        className="sm:col-span-2 inline-flex items-center justify-center gap-3 bg-[#3b82f6] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm hover:bg-[#1e40af] disabled:opacity-60 transition-colors duration-300"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bike className="w-4 h-4" />}
        {loading ? "Submitting..." : "Submit Application"}
      </button>
    </form>
  );
};

export const FormsSection = () => {
  const [tab, setTab] = useState("client");

  return (
    <section id="enquire" className="relative bg-[#111827] py-20 sm:py-28 border-y border-white/10">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#d4af37] mb-4">Get Started</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase text-slate-100 leading-[0.95]">
            Deploy or <span className="text-stroke-gold">Get Deployed</span>
          </h2>
        </motion.div>

        <div className="flex gap-2 mb-8" id="join">
          <button
            data-testid="tab-client-enquiry"
            onClick={() => setTab("client")}
            className={`flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-5 py-3 rounded-sm border transition-colors duration-300 ${
              tab === "client" ? "bg-[#d4af37] text-[#0a0f1d] border-[#d4af37]" : "text-slate-400 border-white/15 hover:border-[#d4af37]/50"
            }`}
          >
            <Building2 className="w-4 h-4" /> Hire Manpower
          </button>
          <button
            data-testid="tab-rider-application"
            onClick={() => setTab("rider")}
            className={`flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-5 py-3 rounded-sm border transition-colors duration-300 ${
              tab === "rider" ? "bg-[#3b82f6] text-white border-[#3b82f6]" : "text-slate-400 border-white/15 hover:border-[#3b82f6]/50"
            }`}
          >
            <Bike className="w-4 h-4" /> Join as Rider / Worker
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="bg-[#0d1424] border border-white/10 rounded-sm p-6 sm:p-9"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.35 }}
            >
              {tab === "client" ? <ClientForm /> : <RiderForm />}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
